import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Stack, Chip, Popover, TextField, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';

const FullscreenChatHeader = ({
  title = 'Chat',
  showReportChip = false,
  onClickReportChip = null,
  messagesCount = 0,
  onHistory,
  onNewChat,
  onDownload,
  externalReport = false,
  onExternalExit,
  onExit,
  onClose,
  hideClose = false,
  threadId = null,
  isPublic = false,
  shareUrl = null,
  onShare = null
}) => {
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShareClick = (event) => {
    if (onShare) {
      onShare();
    } else if (isPublic && shareUrl) {
      setShareAnchorEl(event.currentTarget);
    }
  };

  const handleCloseSharePopover = () => {
    setShareAnchorEl(null);
    setCopied(false);
  };

  const handleCopyUrl = () => {
    if (shareUrl) {
      const fullPath = `/agentic${shareUrl}`;
      const fullUrl = `${window.location.origin}${fullPath}`;
      navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sharePopoverOpen = Boolean(shareAnchorEl);

  return (
    <Box sx={{ p: 1, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Typography variant="h6">{title}</Typography>
          {showReportChip && (
            <Chip size="small" color="success" variant="outlined" label="Report Context" onClick={onClickReportChip || undefined} />
          )}
        </Stack>
        <Box>
          <Tooltip title="New chat">
            <IconButton size="small" onClick={onNewChat} sx={{ mr: 0.5 }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="History">
            <IconButton size="small" onClick={onHistory} sx={{ mr: 0.5 }}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          {messagesCount > 0 && (
            <Tooltip title="Download chat (.docx)">
              <IconButton size="medium" onClick={onDownload} sx={{ mr: 0.5 }}>
                <DownloadOutlined />
              </IconButton>
            </Tooltip>
          )}
          {threadId && (
            <Tooltip title={isPublic ? 'Share link' : 'Share this chat'}>
              <IconButton size="small" onClick={handleShareClick} sx={{ mr: 0.5 }} color={isPublic ? 'primary' : 'default'}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Exit Fullscreen">
            <IconButton size="small" onClick={externalReport ? onExternalExit : onExit} sx={{ mr: hideClose ? 0 : 0.5 }}>
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
          {!hideClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseOutlined />
            </IconButton>
          )}
        </Box>
      </Box>
      {isPublic && shareUrl && (
        <Popover
          open={sharePopoverOpen}
          anchorEl={shareAnchorEl}
          onClose={handleCloseSharePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>
              Share this chat
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={`${window.location.origin}/agentic${shareUrl}`}
              readOnly
              sx={{ mt: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleCopyUrl} edge="end">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                Link copied!
              </Typography>
            )}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default FullscreenChatHeader;
